/**
 * excelTransform.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Transforms a raw row from the student Excel sheet into a clean object
 * ready to POST to /api/students/import.
 *
 * Handles:
 *   - Date conversion  (Excel datetime / YYYY-MM-DD string → DD/MM/YYYY)
 *   - Gender           MALE/FEMALE → Male/Female
 *   - maritalStatus    MARRIED/UNMARRIED → Married/Unmarried
 *   - Urban/Rural      URBAN/RURAL → Urban/Rural
 *   - religion         Inconsistent entries → normalised values
 *   - admissionType    Always "CAP" (per import rule)
 *   - admissionCategory Derived from feeStructureName
 *   - Whitespace trimming on all string fields
 *   - feesDecided / scholarshipAmountBalance → always 0
 */

// ── Date helpers ──────────────────────────────────────────────────────────────

/**
 * Converts any date value from Excel to DD/MM/YYYY string.
 * Excel may give us:
 *   - A JS Date object (openpyxl / SheetJS datetime cell)
 *   - A string "2024-10-19" or "2024/10/19"
 *   - A number (Excel serial date) — SheetJS with { cellDates: false }
 *   - null / undefined
 */
export function convertDate(value) {
  if (!value) return "";

  // Already a JS Date (SheetJS returns these when cellDates: true)
  if (value instanceof Date) {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  // String — try YYYY-MM-DD, YYYY/MM/DD, DD/MM/YYYY
  if (typeof value === "string") {
    const s = value.trim();

    // Already DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

    // YYYY-MM-DD or YYYY/MM/DD
    const isoMatch = s.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      return `${dd}/${mm}/${yyyy}`;
    }
  }

  // Excel serial number fallback (SheetJS cellDates: false)
  if (typeof value === "number") {
    const d = new Date(Math.round((value - 25569) * 86400 * 1000));
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  return "";
}

// ── Text normalisation helpers ─────────────────────────────────────────────────

const str = (v) => (v != null ? String(v).trim() : "");

const titleCase = (v) => {
  const s = str(v);
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
};

/** Normalise gender: MALE → Male, FEMALE → Female */
const normaliseGender = (v) => {
  const s = str(v).toUpperCase();
  if (s === "MALE") return "Male";
  if (s === "FEMALE") return "Female";
  if (s === "OTHER") return "Other";
  return "Male"; // fallback — required field
};

/** Normalise marital status */
const normaliseMarital = (v) => {
  const s = str(v).toUpperCase();
  if (s === "MARRIED") return "Married";
  if (s === "UNMARRIED") return "Unmarried";
  return undefined;
};

/** Normalise Urban/Rural */
const normaliseUrbanRural = (v) => {
  const s = str(v).toUpperCase();
  if (s === "URBAN") return "Urban";
  if (s === "RURAL") return "Rural";
  return undefined;
};

/** Normalise religion to consistent values */
const normaliseReligion = (v) => {
  const s = str(v)
    .toUpperCase()
    .replace(/[\s,]+/g, " ")
    .trim();
  if (s.includes("ISLAM") || s.includes("MUSLIM")) return "Islam";
  if (s.includes("HINDU")) return "Hindu";
  if (s.includes("CHRISTIAN") || s.includes("CHRISTAIN")) return "Christian";
  if (s.includes("BOUDH") || s.includes("BUDDH")) return "Buddhist";
  if (s === "-" || s === "") return "";
  return titleCase(v); // keep as-is but title-cased
};

/**
 * Derive admissionCategory from feeStructureName.
 * The feeStructureName pattern is like: CAP-OBC(F), INST-SC(M), ACAP-OPEN(F) etc.
 * We extract the category part.
 */

const deriveAdmissionCategory = (feeStructureName) => {
  if (!feeStructureName) return "Open";

  const s = str(feeStructureName).toUpperCase();

  if (s.includes("OMS") || s.includes("OBC-MS"))
    return "Open Minority Scholarship";
  if (s.includes("TFWS")) return "TFWS";
  if (s.includes("EWS")) return "EWS";
  if (s.includes("OPEN")) return "Open";
  if (s.includes("OBC")) return "OBC";
  if (s.includes("SC")) return "SC";
  if (s.includes("ST")) return "ST";
  if (s.includes("SBC")) return "SBC";
  if (s.includes("VJ")) return "VJ";
  // NT — we don't know which sub-type from feeStructureName alone, default NT-A
  if (s.includes("NT")) return "NT-A";

  return "Open"; // safe default
};

// ── Main transform function ────────────────────────────────────────────────────

/**
 * Transform a single raw Excel row into a clean student object.
 * @param {object}  row   - Raw object from SheetJS (keys = column headers)
 * @param {number}  index - Row index (1-based) used for placeholder IDs
 * @returns {object} cleaned student ready for the import API
 */
export function transformRow(row, index) {
  // Placeholders for missing required fields
  const enrollmentNo = row.enrollmentNo
    ? String(row.enrollmentNo).trim()
    : `TEMP-${index}`;

  const aadharNo = row.aadharNo
    ? String(row.aadharNo).trim().replace(/\s/g, "")
    : "000000000000";

  return {
    // ── System ────────────────────────────────────────────────────────────────
    status: str(row.status) || "Active",

    // ── Identity ──────────────────────────────────────────────────────────────
    institueId: row.institueId != null ? Number(row.institueId) : undefined,
    firstName: str(row.firstName),
    middleName: str(row.middleName),
    lastName: str(row.lastName),
    fullName: str(row.fullName),
    bloodGroup: str(row.bloodGroup) || undefined,
    fatherName: str(row.fatherName),
    motherName: str(row.motherName),
    gender: normaliseGender(row.gender),
    maritalStatus: normaliseMarital(row.maritalStatus),

    // ── Academic ──────────────────────────────────────────────────────────────
    courseAdmitted: str(row.courseAdmitted),
    courseYear: str(row.courseYear),
    enrollmentNo,
    admissionType: "CAP", // always CAP per import rule
    admissionCategory: deriveAdmissionCategory(row.feeStructureName),
    generalRegNo:
      row.generalRegNo != null ? Number(row.generalRegNo) : undefined,
    rollNo: row.rollNo != null ? Number(row.rollNo) : undefined,
    instituteAdmissionDate: convertDate(row.instituteAdmissionDate),
    currentAdmissionDate: convertDate(row.currentAdmissionDate),
    previousPassingYear: str(row.previousPassingYear),
    divisionName: str(row.divisionName),
    feeStructureName: str(row.feeStructureName),
    studentType: str(row.studentType) || undefined,
    firstYearStream: str(row.firstYearStream),
    previousSchoolName: str(row.previousSchoolName),

    // ── Fees — always 0 ───────────────────────────────────────────────────────
    feesDecided: 0,
    scholarshipAmountBalance: 0,

    // ── Identity documents ────────────────────────────────────────────────────
    aadharNo,
    aaparId: str(row.aaparId),
    dob: convertDate(row.dob),

    // ── Personal background ───────────────────────────────────────────────────
    nationality: str(row.nationality) || "Indian",
    motherTongue: str(row.motherTongue),
    religion: normaliseReligion(row.religion),
    caste: str(row.caste),
    subCaste: str(row.subCaste) || "–",
    isBplCardHolder:
      row.isBplCardHolder != null ? Boolean(row.isBplCardHolder) : undefined,
    bplCardNo: str(row.bplCardNo),
    rationCardNo: str(row.rationCardNo),

    // ── Birth location ────────────────────────────────────────────────────────
    placeOfBirth: str(row.placeOfBirth),
    birthDistrict: str(row.birthDistrict),
    birthTaluka: str(row.birthTaluka),
    birthState: str(row.birthState),
    birthCountry: str(row.birthCountry) || "India",
    birthUrbanRural: normaliseUrbanRural(row.birthUrbanRural),

    // ── Contact & family ──────────────────────────────────────────────────────
    mobile: str(row.mobile),
    fatherMobile: str(row.fatherMobile),
    motherMobile: str(row.motherMobile),
    telephoneNo: str(row.telephoneNo),
    parentAddress: str(row.parentAddress),
    parentIncome:
      row.parentIncome != null ? Number(row.parentIncome) : undefined,
    motherIncome:
      row.motherIncome != null ? Number(row.motherIncome) : undefined,
    parentEmailAddress: str(row.parentEmailAddress),
    parentOccupation: str(row.parentOccupation),
    motherOccupation: str(row.motherOccupation),
    fatherDob: convertDate(row.fatherDob),
    motherDob: convertDate(row.motherDob),
    fatherEduction: str(row.fatherEduction),
    motherEducation: str(row.motherEducation),
    email: str(row.email),

    // ── Address ───────────────────────────────────────────────────────────────
    address: str(row.address),
    city: str(row.city),
    taluka: str(row.taluka),
    addressUrbanRural: normaliseUrbanRural(row.addressUrbanRural),
    district: str(row.district),
    state: str(row.state),
    pincode: row.pincode != null ? String(row.pincode).trim() : "",
    country: str(row.country) || "India",

    // ── Bank ──────────────────────────────────────────────────────────────────
    bankAccountNo: str(row.bankAccountNo),
    bankName: str(row.bankName),
    branchName: str(row.branchName),
    ifscCode: str(row.ifscCode),
  };
}
